import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useLocation, useNavigate } from 'react-router'

import { ApiError, apiRequest, invalidateSessionRequests, loginPathFor, setCsrfToken, setUnauthorizedHandler } from '../lib/apiClient'
import type { SessionResponse } from '../lib/apiTypes'
import { SessionContext, type SessionContextValue, type SessionStatus } from './sessionContext'

export function SessionProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate()
  const location = useLocation()
  const queryClient = useQueryClient()
  const identity = useRef('')
  const [status, setStatus] = useState<SessionStatus>('loading')
  const [user, setUser] = useState<SessionContextValue['user']>(null)
  const [error, setError] = useState<unknown>(null)
  const [attempt, setAttempt] = useState(0)
  const [totpEnrollmentRequired, setTotpEnrollmentRequired] = useState(false)
  const [totpRequired, setTotpRequired] = useState(false)

  const clearPrivateData = useCallback(() => {
    invalidateSessionRequests()
    void queryClient.cancelQueries()
    queryClient.clear()
  }, [queryClient])

  const signIn = useCallback((session: SessionResponse) => {
    const nextIdentity = JSON.stringify([session.user.id, session.user.role, session.user.merchant_scope_restricted, session.user.merchant_ids, session.totp_enrollment_required])
    if (identity.current !== nextIdentity) clearPrivateData()
    identity.current = nextIdentity
    setCsrfToken(session.csrf_token)
    setUser(session.user)
    setTotpEnrollmentRequired(Boolean(session.totp_enrollment_required))
    setTotpRequired(Boolean(session.totp_required))
    setError(null)
    setStatus('authenticated')
  }, [clearPrivateData])

  const markExpired = useCallback(() => {
    clearPrivateData()
    identity.current = ''
    setUser(null)
    setCsrfToken('')
    setTotpEnrollmentRequired(false)
    setStatus('anonymous')
  }, [clearPrivateData])

  useEffect(() => {
    const controller = new AbortController()
    let active = true
    apiRequest<SessionResponse>('/auth/me', { signal: controller.signal })
      .then((session) => { if (active) signIn(session) })
      .catch((cause: unknown) => {
        if (!active || (cause instanceof DOMException && cause.name === 'AbortError')) return
        markExpired()
        if (cause instanceof ApiError && cause.status === 401) {
          setError(null)
        } else {
          setError(cause)
          setStatus('unavailable')
        }
      })
    return () => { active = false; controller.abort() }
  }, [attempt, signIn, markExpired])

  const redirectToLogin = useCallback(() => {
    navigate(loginPathFor(location), { replace: true })
  }, [navigate, location])

  useEffect(() => {
    setUnauthorizedHandler(markExpired)
    return () => setUnauthorizedHandler(null)
  }, [markExpired])

  useEffect(() => {
    if (status === 'anonymous' && location.pathname !== '/login') redirectToLogin()
  }, [status, location.pathname, redirectToLogin])

  const logout = useCallback(async () => {
    try {
      await apiRequest<void>('/auth/logout', { method: 'POST' })
    } catch (cause) {
      if (!(cause instanceof ApiError && cause.status === 401)) throw cause
    }
    markExpired()
    navigate('/login', { replace: true })
  }, [navigate, markExpired])

  const value = useMemo<SessionContextValue>(() => ({
    status, user, error, totpEnrollmentRequired, totpRequired,
    role: user?.role ?? null,
    canWrite: user?.role === 'admin' && !totpEnrollmentRequired,
    reload: () => setAttempt((current) => current + 1),
    signIn, redirectToLogin, logout, markExpired,
  }), [status, user, error, totpEnrollmentRequired, totpRequired, signIn, redirectToLogin, logout, markExpired])

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
}
